import React, { useEffect, useState } from "react";
import axios from "axios";
import { BasketItem, SubmissionResult } from "./types";
import useSummary from "../../hooks/useSummary";
import useBranches from "../../hooks/useBranches";
import useCurrencies from "../../hooks/useCurrencies";
import basketItemsToReturnRequest from "./basketItemsToReturnRequest";
import useDefaultRequiredDate from "../../hooks/useDefaultRequiredDate";
import useErrorHandler from "../../hooks/userErrorHandler";
import PageContainer from "../PageContainer";
import useBranch from "../../hooks/useBranch";
import usePermissions from "../../hooks/usePermissions";
import MessageBox from "../../components/MessageBox";
import NewOrderAndReturn from "../../components/NewOrderAndReturn";
import responseToSubmissionResultItems from "../../utils/responseToSubmissionResultItems";
import { resetTimeout } from "../TimeoutContainer/TimeoutProvider";

const NewReturnContainer = () => {
  const [isFcaCurrency, setIsFcaCurrency] = useState<boolean>(false);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<string>();
  const [branchId, setBranchId] = useState<number>();
  const [companyId, setCompanyId] = useState<string>();
  const permissions = usePermissions();
  const { branch } = useBranch(companyId, branchId, permissions, 0, 0);
  const [selectedCurrencyAmount, setSelectedCurrencyAmount] = useState<number>(
    0
  );
  const [settledInSameCurrency, setSettledInSameCurrency] = useState<string>(
    "0"
  );
  const [basketItems, setBasketItems] = useState<BasketItem[]>([]);
  const [
    submissionResultItems,
    setSubmissionResultItems
  ] = useState<SubmissionResult | null>(null);
  const [
    openCurrencyUnavailableSnackBar,
    setOpenCurrencyUnavailableSnackBar
  ] = useState<boolean>(false);

  const setError = useErrorHandler();

  const { branches, branchesLoading } = useBranches();
  const { currencies, currenciesLoading } = useCurrencies();
  const { summary } = useSummary(
    selectedCurrencyId,
    selectedCurrencyAmount,
    settledInSameCurrency,
    branch && branch.company && branch.company.id,
    true
  );
  const {
    defaultRequiredDate,
    defaultRequiredDateLoading
  } = useDefaultRequiredDate([basketItems]);

  function doProcessWithReferees(ordersWithReferees: any) {
    (async () => {
      try {
        if (ordersWithReferees !== [] || null) {
          ordersWithReferees.forEach(async (order: any) => {
            await axios.put(
              `/api/Orders/${order.id}/referee`,
              { version: order.version, referee: order.userDetails.id },
              {
                headers: {
                  "Content-Type": "application/json"
                }
              }
            );
          });
        }
      } catch (err) {
        if (
          err &&
          err.response &&
          err.response.status === 409 &&
          err.response.data.problems &&
          err.response.data.problems[0].problemKey ===
            "Return.Currency.Unavailable"
        ) {
          setOpenCurrencyUnavailableSnackBar(true);
        } else if (err.message === "Network Error") {
          document.location.href = "/";
        }
      }
    })();
  }

  useEffect(() => {
    if (
      selectedCurrencyId !== "" &&
      companyId !== "" &&
      companyId != undefined &&
      selectedCurrencyId != undefined
    ) {
      (async () => {
        try {
          const response = await axios.get(
            `/api/Companies/${companyId}?ts= + ${new Date().getTime()}`
          );
          setIsFcaCurrency(
            response.data.fcaCurrencies.some(
              (fca: string) => fca === selectedCurrencyId
            )
          );
          resetTimeout();
        } catch (err) {
          if (err.message === "Network Error") {
            document.location.href = "/";
          }
          setError("errorTitle", err);
        }
      })();
    }
  }, [companyId, branchId, selectedCurrencyId]);

  if (!branches) return null;
  if (!currencies) return null;
  return (
    <PageContainer title="New Return">
      {defaultRequiredDate && (
        <NewOrderAndReturn
          returns={true}
          deliveryAddress={branch}
          onSelectBranch={(branch: any) => {
            setBranchId(branch.id);
            setCompanyId(branch.companyId);
          }}
          isLoadingInitialData={
            branchesLoading || currenciesLoading || defaultRequiredDateLoading
          }
          defaultRequiredDate={defaultRequiredDate}
          branches={branches}
          currencies={currencies}
          onCurrencyChange={setSelectedCurrencyId}
          onCurrencyAmountChange={setSelectedCurrencyAmount}
          onSettlementSourceChange={setSettledInSameCurrency}
          summary={summary}
          basketItems={basketItems}
          onAddToBasket={(basketItem: BasketItem) => {
            setBasketItems(() => [...basketItems, basketItem]);
          }}
          onDeleteFromBasket={(itemsForDeletion: BasketItem[]) => {
            const filteredItems = basketItems.filter(
              (item: BasketItem) => !itemsForDeletion.includes(item)
            );
            setBasketItems(filteredItems);
          }}
          submissionResultItems={submissionResultItems}
          onReferredOrders={(referees: any[]) => {
            doProcessWithReferees(referees);
          }}
          onConfirmOrder={async () => {
            try {
              const response = await axios.post(
                "/api/Orders/currency-returns",
                {
                  orders: basketItemsToReturnRequest(basketItems)
                }
              );
              setBasketItems([]);
              setSubmissionResultItems({
                ordered: responseToSubmissionResultItems(response.data.ordered),
                requiresAuthorisation: responseToSubmissionResultItems(
                  response.data.requiresAuthorisation
                ),
                failed: responseToSubmissionResultItems(response.data.failed)
              });
            } catch (err) {
              setError("Unable to submit return. Please try again.", err);
            }
          }}
          isFcaCurrency={isFcaCurrency}
        />
      )}
      {openCurrencyUnavailableSnackBar ? (
        <MessageBox
          severity="error"
          onClose={() => setOpenCurrencyUnavailableSnackBar(false)}
          message={"The currency selected is currently unavailable."}
        />
      ) : (
        <></>
      )}
    </PageContainer>
  );
};

export default NewReturnContainer;
